// @flow
import React from 'react';
import styled from 'styled-components';
import * as Buttons from 'styles/Buttons';
import ButtonUploadFile from 'components/shared/ButtonUploadFileComponent';
import DropZoneUploadFile from 'components/shared/DropZoneUploadFileComponent';
import FileLoadComponent from 'components/shared/FileLoadComponent';
import Asset from 'models/homes/Asset';
import { media } from 'styles/Mixins';
import * as Layout from 'styles/Layout';

const Container = styled(Layout.FlexColumn)`
  position: relative;
  align-items: center;
  width: 100%;
  border: ${({ isDragIn = false, color }) =>
    isDragIn ? `2px solid ${color}` : `2px dashed ${color}`}};
  border-radius: 2px;
  padding: 4px 7px 20px 7px;
  background: ${({ isDragIn = false }) => (isDragIn ? '#E8F0FE' : '#ffffff')};
`;
export const SubmitButton = styled(Buttons.PrimaryButton)`
  width: 100%;
`;

const InstructionText = styled.span`
  display: none;
  min-height: 19px;
  font-size: 16px;
  font-weight: bold;
  line-height: 19px;
  text-align: center;
  margin-bottom: 15px;
  color: ${({ haveError, color }) => (haveError ? '#FF1A00' : color)};
  z-index: 2;
  ${media.md`
    display: block;
  `}
`;

const IconContainer = styled(Layout.FlexRow)`
  justify-content: center;
  align-items: center;
  min-height: 50px;
  width: 100%;
  margin-top: 14px;
  margin-bottom: 10px;
  z-index: 2;
`;

const ButtonUploadFileContainer = styled.div`
  width: 200px;
  z-index: 2;
`;

const DropZoneUploadFileContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: ${({ isDragIn = false }) => (isDragIn ? '3' : '1')};
`;
const FilesContainer = styled(Layout.FlexRow)`
  width: 100%;
  flex-wrap: wrap;
  justify-content: ${props => props.justifyContent};
  margin-bottom: 10px;
  z-index: 2;
  ${({ scrollable }) =>
    scrollable &&
    `
      height: 250px;
      overflow-y: scroll;
      scrollbar-width: none;
      ::-webkit-scrollbar {
        display: none;
      }
  `}
`;

const ErrorMessage = styled.span`
  display: block;
  overflow: hidden;
  height: ${props => (props.visible ? 'auto' : '0')};
  width: 100%;
  color: #ff1a00;
  font-size: 12px;
  line-height: 15px;
  margin-top: ${props => (props.visible ? '4px' : '0')};
  margin-bottom: 30px;
  transition: all 0.2s linear;
`;

type StateType = {
  isDragIn: boolean,
  isError: boolean,
  errorMessage: string
};

type PropsType = {
  Icon: any,
  IconError: any,
  instruction: string,
  textButton?: string,
  showAssetsLoaded: boolean,
  assetsLoaded: Array<Asset>,
  uploadFileAction: (File, string) => void,
  onSelectFiles: (files: Array<File>) => void,
  onDeleteFileLoaded: (id: string) => void,
  haveError: boolean,
  acceptFile?: string,
  isMultiple?: boolean,
  scrollable?: boolean,
  color?: string,
  isError: boolean,
  errorMessage: string
};
let dropRef = React.createRef();
export default class AutoUploadFilesComponent extends React.Component<
  PropsType,
  StateType
> {
  static defaultProps = {
    isMultiple: true,
    showAssetsLoaded: true,
    color: '#278C7E'
  };
  constructor(props: PropsType) {
    super(props);
    this.state = {
      isDragIn: false,
      isError: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    let div = dropRef.current;
    if (div) {
      div.addEventListener('dragenter', this.handleDragIn);
      div.addEventListener('dragleave', this.handleDragOut);
      div.addEventListener('drop', this.handleDrag);
    }
  }

  componentWillUnmount() {
    let div = dropRef.current;
    if (div) {
      div.removeEventListener('dragenter', this.handleDragIn);
      div.removeEventListener('dragleave', this.handleDragOut);
      div.removeEventListener('drop', this.handleDrag);
    }
  }

  handleDragIn = (e: any) => {
    this.setState({ isDragIn: true });
  };
  handleDragOut = (e: any) => {
    this.setState({ isDragIn: false });
  };
  handleDrag = (e: any) => {
    if (this.state.isDragIn) {
      this.setState({ isDragIn: false });
    }
  };
  handleErrorMessage = (message: string) => {
    this.setState({ isError: true, errorMessage: message });
  };
  handleClearError = () => {
    this.setState({ isError: false, errorMessage: '' });
  };
  handleOnDeleteFileLoaded = (id: string) => {
    this.props.onDeleteFileLoaded(id);
    this.handleClearError();
  };

  render() {
    let {
      Icon,
      IconError = Icon,
      instruction,
      uploadFileAction,
      onSelectFiles,
      haveError,
      assetsLoaded,
      isMultiple,
      textButton = 'Añadir fotos',
      acceptFile,
      showAssetsLoaded,
      scrollable = false,
      color
    } = this.props;
    return (
      <>
        <Container isDragIn={this.state.isDragIn} color={color}>
          <IconContainer
            isVisible={assetsLoaded.length == 0 && !haveError ? true : false}
          >
            <Icon />
          </IconContainer>
          <IconContainer
            isVisible={assetsLoaded.length == 0 && haveError ? true : false}
          >
            <IconError />
          </IconContainer>
          <FilesContainer
            scrollable={scrollable}
            isVisible={assetsLoaded.length > 0 && showAssetsLoaded}
          >
            {assetsLoaded.map((asset, index) => (
              <FileLoadComponent
                key={index}
                asset={asset}
                uploadFileAction={uploadFileAction}
                onDeleteFileLoaded={this.handleOnDeleteFileLoaded}
                loadingColor={color}
              />
            ))}
          </FilesContainer>
          <InstructionText haveError={haveError} color={color}>
            {instruction}
          </InstructionText>
          <ButtonUploadFileContainer>
            <ButtonUploadFile
              text={textButton}
              accept={acceptFile}
              onSelectFiles={onSelectFiles}
              multipleFile={isMultiple}
              color={color}
              onError={this.handleErrorMessage}
              clearError={this.handleClearError}
            />
          </ButtonUploadFileContainer>
          <DropZoneUploadFileContainer
            innerRef={dropRef}
            isDragIn={this.state.isDragIn}
          >
            <DropZoneUploadFile
              accept={acceptFile}
              onSelectFiles={onSelectFiles}
              multipleFile={isMultiple}
              onError={this.handleErrorMessage}
              clearError={this.handleClearError}
            />
          </DropZoneUploadFileContainer>
        </Container>
        <ErrorMessage visible={this.state.isError}>
          {this.state.isError ? this.state.errorMessage : ''}
        </ErrorMessage>
      </>
    );
  }
}
